import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import usePaginationValuesWhere from '../../hooks/usePaginationValuesWhere';
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { GridView, SvgComponent } from 'components';
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { ItemManager } from "managers";
import Select from "react-select";

const DEFAULT_PAGINATION_VALUES = {
  EsPaginable: true,
  CantidadPorPagina: 15,
  EsOrdernable: true,
  IndiceDePagina: 0,
  PaginasTotal: 1,
};

export default function ItemList({ hideLoading }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const { cerrarAlertas } = useMessage();
  const history = useHistory();

  const INDICADOR_FACTURACION_LIST = [
    { value: null, label: "Todos" },
    { value: 0, label: "No Facturable" },
    { value: 1, label: "ITBIS 18%" },
    { value: 2, label: "ITBIS 16%" },
    { value: 3, label: "ITBIS 0%" },
    { value: 4, label: "Exento" },
    { value: 5, label: "ITBIS 18% del 10 %" },
  ];

  const INDICADOR_BIEN_O_SERVICIO_LIST = [
    { value: null, label: "Todos" },
    { value: 0, label: "Bien" },
    { value: 1, label: "Servicio" },
  ];

  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    { value: "A", label: "Activo" },
    { value: "I", label: "Inactivo" },
  ];

  useEffect(() => {
    buscar();
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where]);

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };
    ItemManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        const transformedList = res.Data.map(item => ({
          ...item,
          IndicadorFacturacion: getFacturacionTexto(item.IndicadorFacturacion),
          IndicadorBienOServicio: item.IndicadorBienOServicio === 0 ? 'Bien' : 'Servicio',
          Estatus: item.Estatus === 'A' ? 'Activo' : 'Inactivo'
        }));
        setList(transformedList);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (params = {}) => {
    const { IdSec } = params;
    let urlBase = `/ItemEdit?`;
    if (IdSec) {
      urlBase += `IdSec=${IdSec}`;
    }
    history.push(urlBase);
  };

  const getFacturacionTexto = (indicador) => {
    switch (indicador) {
      case 0:
        return 'No Facturable';
      case 1:
        return 'ITBIS 18%';
      case 2:
        return 'ITBIS 16%';
      case 3:
        return 'ITBIS 0%';
      case 4:
        return 'Exento';
      case 5:
        return 'ITBIS 18% del 10 %';
    }
  }

  return (
    <div>
      <div>
        <ListHeader titulo={"Items"} crearNuevo={handleClick}></ListHeader>
      </div>
      <>
        <GridView
          pagination={pagination}
          setPagination={setPagination}
          allowPaging={pagination.EsPaginable}
          pageSize={pagination.CantidadPorPagina}
          pageIndex={pagination.IndiceDePagina}
          lastPageNumber={pagination.PaginasTotal}
          dataSource={list}
          onPageIndexChanging={onPageIndexChanging}
          where={where}
          setWhere={setWhere}
        >
          <GridView.Column
            isField
            width="100px"
            showClearFilter={true}
            bodyRenderer={(row) => {
              return (
                <div className="float-right">
                  <IconButton onClick={() => handleClick({ IdSec: row.IdSec })}>
                    <ToolTip message="Editar">
                      <div>
                        <SvgComponent
                          iconName="pencil"
                          height="25"
                          width="25"
                        />
                      </div>
                    </ToolTip>
                  </IconButton>
                </div>
              );
            }}
          />
          <GridView.Column
            headerText="Id"
            dataField="IdSec"
            sortExpression="IdSec"
            width="110px"
          />
          <GridView.Column
            headerText="Nombre"
            dataField="Nombre"
            sortExpression="Nombre"
            width="250px"
          />
          <GridView.Column
            headerText="Descripción"
            dataField="Descripcion"
            sortExpression="Descripcion"
            width="300px"
          />
          <GridView.Column
            headerText="Indicador Facturación"
            dataField="IndicadorFacturacion"
            sortExpression="IndicadorFacturacion"
            width="200px"
            filterRenderer={() => {
              const selectedValue = INDICADOR_FACTURACION_LIST.find(
                (option) => option.value === where.IndicadorFacturacion
              );

              return (
                <Select
                  className="react-select primary"
                  options={INDICADOR_FACTURACION_LIST}
                  value={selectedValue}
                  name="IndicadorFacturacion"
                  onChange={(selectedOption) => {
                    setWhere({
                      ...where,
                      IndicadorFacturacion: selectedOption ? selectedOption.value : null,
                    });
                  }}
                />
              );
            }}
          />
          <GridView.Column
            headerText="Indicador Bien o Servicio"
            dataField="IndicadorBienOServicio"
            sortExpression="IndicadorBienOServicio"
            width="210px"
            filterRenderer={() => {
              const selectedValue = INDICADOR_BIEN_O_SERVICIO_LIST.find(
                (option) => option.value === where.IndicadorBienOServicio
              );

              return (
                <Select
                  className="react-select primary"
                  options={INDICADOR_BIEN_O_SERVICIO_LIST}
                  value={selectedValue}
                  name="IndicadorBienOServicio"
                  onChange={(selectedOption) => {
                    setWhere({
                      ...where,
                      IndicadorBienOServicio: selectedOption ? selectedOption.value : null,
                    });
                  }}
                />
              );
            }}
          />
          <GridView.Column
            headerText="Precio Unitario"
            dataField="PrecioUnitario"
            sortExpression="PrecioUnitario"
            width="150px"
          />
          <GridView.Column
            headerText="Estatus"
            dataField="Estatus"
            sortExpression="Estatus"
            width="175px"
            filterRenderer={() => {
              return (
                <Select
                  className="react-select primary"
                  options={ESTATUS_LIST}
                  value={ESTATUS_LIST.filter(function (option) {
                    return (
                      option.value ===
                      (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                    );
                  })}
                  name="Estatus"
                  onChange={(e) =>
                    setWhere({
                      ...where,
                      Estatus: e.value,
                    })
                  }
                />
              );
            }}
          />
        </GridView>
      </>
    </div>
  );
}